import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export enum FeedbackTabs {
	COMMENT = 'comment',
	RUBRIC = 'rubric',
	INFORMATION = 'information',
	TRANSCRIPTION = 'transcription',
	ANALYTICS = 'analytics',
}

@Injectable({
	providedIn: 'root'
})
export class FeedbackSessionService {
	private readonly isPanelOpenSubject = new BehaviorSubject<boolean>(false);
	private readonly activeTabSubject = new BehaviorSubject<FeedbackTabs>(
		FeedbackTabs.COMMENT
	);
	private readonly oneClickMarkerEnabledSubject = new BehaviorSubject<boolean>(false);
	private readonly autoPlayEnabledSubject = new BehaviorSubject<boolean>(true);

	public readonly isPanelOpen$: Observable<boolean> = this.isPanelOpenSubject.asObservable();
	public readonly activeTab$: Observable<FeedbackTabs> = this.activeTabSubject.asObservable();

	constructor () {}

	public togglePanel (value?: boolean): void {
		const currentValue = this.isPanelOpenSubject.getValue();
		this.isPanelOpenSubject.next(value ?? !currentValue);
	}

	public setActiveTab (tab: FeedbackTabs): void {
		this.activeTabSubject.next(tab);
	}

	public toggleOneClickMarker (value?: boolean): void {
		const currentValue = this.oneClickMarkerEnabledSubject.getValue();
		this.oneClickMarkerEnabledSubject.next(value ?? !currentValue);
		this.setLocalStorage('oneClickMarker', value ?? !currentValue);
	}

	public toggleAutoPlay (value?: boolean): void {
		const currentValue = this.autoPlayEnabledSubject.getValue();
		this.autoPlayEnabledSubject.next(value ?? !currentValue);
		this.setLocalStorage('autoPlay', value ?? !currentValue);
	}

	public getCurrentTab (): FeedbackTabs {
		return this.activeTabSubject.getValue();
	}

	public getIsPanelOpen (): boolean {
		return this.isPanelOpenSubject.getValue();
	}

	public getOneClickMarkerEnabled (): boolean {
		return this.oneClickMarkerEnabledSubject.getValue();
	}

	public getAutoPlayEnabled (): boolean {
		return this.autoPlayEnabledSubject.getValue();
	}

	public initializeState (): void {
		const feedbackSettings = this.getFeedbackSettings();
		if ('oneClickMarker' in feedbackSettings) {
			this.oneClickMarkerEnabledSubject.next(feedbackSettings.oneClickMarker);
		}
		if ('autoPlay' in feedbackSettings) {
			this.autoPlayEnabledSubject.next(feedbackSettings.autoPlay);
		}
	}

	private setLocalStorage (key: string, value: any): void {
		const feedbackSettings = this.getFeedbackSettings();
		feedbackSettings[key] = value;
		localStorage.setItem('feedbackSettings', JSON.stringify(feedbackSettings));
	}

	private getFeedbackSettings (): any {
		const savedFeedbackSettings = localStorage.getItem('feedbackSettings');
		let feedbackSettings = savedFeedbackSettings ? JSON.parse(savedFeedbackSettings) : {};
		// if feedbackSettings is not an object, set it to an empty object
		if (typeof feedbackSettings !== 'object') {
			feedbackSettings = {};
		}
		return feedbackSettings;
	}
}
